/**
 * Bookingsidens URL-tilstand — fanen, og de seks filtrene i artistregisteret.
 *
 * =================================================================================================
 * HVORFOR FILTRENE FLYTTET UT AV `useState` (QA-runde 12, #24d).
 *
 * Atle: «Flervalg i alle filtre … Valgene ligger i URL-en.»
 *
 * Den setningen er ikke bare en bekvemmelighet. Registeret har 741 artister, og «hvilke ti er verdt
 * å ringe» er et spørsmål man stiller sammen med noen: «se på disse» er en LENKE. Med filtrene i
 * komponentens tilstand finnes den lenka ikke, og en omlasting midt i arbeidet er å begynne på
 * nytt. Kontakter, Oppgaver og Oppsummering har alle sin i adressen, og dette er den samme regelen.
 *
 * =================================================================================================
 * FORMEN ER `tasks/search-params.ts` SIN, OG DET ER MED VILJE.
 *
 * Kommaliste per parameter (`?genre=pop,rock`), fravær betyr standard, ukjente verdier DROPPES
 * framfor å bli en feil. Den siste er den viktigste: en URL er noe folk redigerer for hånd og noe
 * gamle bokmerker bærer, og `?status=kjempebra` skal tegne registeret, ikke en feilside.
 *
 * Det som er nytt her er at to av filtrene — sjanger og artisttype — er FRITEKST. Registeret
 * bestemmer selv hvilke sjangre som finnes (prinsipp 9), så det finnes ingen liste å validere mot.
 * De valideres på lengde og antall, som er det kontrakten også gjør, og en verdi som ikke finnes i
 * registeret gir null treff — som er det ærlige svaret på et filter for noe ingen har.
 */
import {
  BOOKING_ARTIST_STATUSES,
  BOOKING_GENDERS,
  NO_AGENCY_FILTER,
  type BookingArtistStatus,
  type BookingGender,
} from '@tvitvi/shared';

import { validateMoveSearch, type MoveSearch } from './agency-moves-search';

export const BOOKING_TABS = ['artists', 'programming', 'dialogue', 'moves', 'marketplace'] as const;
export type BookingTab = (typeof BOOKING_TABS)[number];

const DEFAULT_TAB: BookingTab = 'artists';

/**
 * Markedsplassen er skjult på Booking til den er klar (Atle, 4. november, sak 329). Fanen, siden og
 * API-et står; `true` viser dem igjen. Et gammelt bokmerke med `?tab=marketplace` lander på Artister.
 */
export const MARKETPLACE_SHOWN = false as boolean;

/** Den samme grensen kontrakten setter, så et for langt filter aldri blir en 400. */
const MAX_VALUES = 20;
const MAX_TEXT = 60;
const MAX_SEARCH = 120;

/**
 * Det `validateSearch` produserer og det `navigate({ search })` må få tilbake.
 *
 * Alle listene er KOMMASTRENGER og ikke arrayer, av samme grunn som i Oppgaver: det er den kortere
 * adressen, det er formen serveren tar imot, og ruteren rundtripper en streng uten å finne på
 * sitering. En array ville stått som `?genre=%5B%22pop%22%5D` i adressefeltet.
 */
/** «Overganger» (28. september) har sine egne filtre, med m-prefiks. Se `agency-moves-search.ts`. */
export interface BookingSearch extends MoveSearch {
  tab?: BookingTab;
  q?: string;
  agency?: string;
  genre?: string;
  type?: string;
  gender?: string;
  status?: string;
  /** «Uten pris» (#24f). Aldri skrevet som `false` — et avslått filter er ikke en opplysning. */
  noPrice?: boolean;
  /** Køen av uavklarte identiteter (sak 89). Samme regel: bare `true` skrives. */
  needsIdentity?: boolean;
  /** «Skjulte» (sak 237.1): bare artistene organisasjonen har skjult. Bare `true` skrives. */
  hidden?: boolean;
  /** «Bare de jeg sammenligner» (QA13 24). Id-ene ligger i nettleseren; adressen sier bare at filteret er på. */
  compared?: boolean;
  /** «Venter på meg/oss» (C9): artister med en kontrakt som venter på en signatur herfra. Bare `true` skrives. */
  waiting?: boolean;
  /** Prisspennet, i minste enhet. Skrives bare når enden faktisk er satt. */
  minCost?: number;
  maxCost?: number;
  /**
   * DYPLENKEN FRA EN TRÅD (QA14 #8, Atles avgjørelse 185).
   *
   * «Knappen tar deg til riktig organisasjon og riktig fane, med raden åpen.» Fram til nå fantes
   * det ingen adresse å ta deg TIL: /booking hadde bare `tab`, så en knapp i trådheaderen kunne
   * peke på sida og ikke på raden.
   *
   * To felt og ikke ett, fordi de to husene har hver sin rad for den samme samtalen: arrangøren
   * har et `booking_bid`, byrået et `booking_enquiry`. En delt tråd gir hver leser id-en som
   * finnes i DERES hus — se `counterpartyLink` på tråd-DTO-en.
   */
  enquiry?: string;
  bid?: string;
  /** 260.6: utkastet Programmering viser, så en omlasting står på det samme utkastet. */
  draft?: string;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Kommaformen og arrayformen, begge inn. Tomme ledd faller bort; rekkefølgen beholdes. */
function parts(value: unknown): string[] {
  const raw = Array.isArray(value) ? value : typeof value === 'string' ? value.split(',') : [];
  const found = raw
    .map((part) => (typeof part === 'string' ? part.trim() : ''))
    .filter((part) => part !== '');
  return [...new Set(found)].slice(0, MAX_VALUES);
}

function isStatus(value: string): value is BookingArtistStatus {
  return (BOOKING_ARTIST_STATUSES as readonly string[]).includes(value);
}

function isGender(value: string): value is BookingGender {
  return (BOOKING_GENDERS as readonly string[]).includes(value);
}

/** Et heltall i adressen, sjekket for FORM. En tekst der er ingen grense, og heller ingen 400. */
function parseInteger(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isInteger(value) && value >= 0) return value;
  if (typeof value !== 'string' || !/^\d{1,12}$/.test(value)) return undefined;
  return Number(value);
}

/**
 * Validert i kanten av ruteren, som i Oppgaver: ingen komponent nedstrøms ser en status som ikke
 * finnes eller en byrå-id som ikke er en uuid.
 *
 * `none` ER EN LOVLIG BYRÅVERDI og ikke en uuid — den betyr «uten byrå», og uten unntaket her
 * hadde det ene filtervalget som ikke peker på en rad blitt kastet på hver sidelasting.
 */
export function validateBookingSearch(raw: Record<string, unknown>): BookingSearch {
  const search: BookingSearch = {};

  const tab = raw['tab'];
  search.tab =
    typeof tab === 'string' &&
    (BOOKING_TABS as readonly string[]).includes(tab) &&
    (tab !== 'marketplace' || MARKETPLACE_SHOWN)
      ? (tab as BookingTab)
      : DEFAULT_TAB;

  const q = raw['q'];
  if (typeof q === 'string' && q.trim() !== '') search.q = q.trim().slice(0, MAX_SEARCH);

  const agency = parts(raw['agency']).filter(
    (value) => value === NO_AGENCY_FILTER || UUID.test(value),
  );
  if (agency.length > 0) search.agency = agency.join(',');

  const genre = parts(raw['genre']).map((value) => value.slice(0, MAX_TEXT));
  if (genre.length > 0) search.genre = genre.join(',');

  const type = parts(raw['type']).map((value) => value.slice(0, MAX_TEXT));
  if (type.length > 0) search.type = type.join(',');

  const gender = parts(raw['gender']).filter(isGender);
  if (gender.length > 0) search.gender = gender.join(',');

  const status = parts(raw['status']).filter(isStatus);
  if (status.length > 0) search.status = status.join(',');

  if (raw['noPrice'] === true || raw['noPrice'] === 'true') search.noPrice = true;
  if (raw['needsIdentity'] === true || raw['needsIdentity'] === 'true') search.needsIdentity = true;
  if (raw['hidden'] === true || raw['hidden'] === 'true') search.hidden = true;
  if (raw['compared'] === true || raw['compared'] === 'true') search.compared = true;
  if (raw['waiting'] === true || raw['waiting'] === 'true') search.waiting = true;

  /* Bare en uuid slipper inn: en dyplenke er en id, og alt annet er støy eller et forsøk. */
  const enquiry = raw['enquiry'];
  if (typeof enquiry === 'string' && UUID.test(enquiry)) search.enquiry = enquiry;
  const bid = raw['bid'];
  if (typeof bid === 'string' && UUID.test(bid)) search.bid = bid;
  const draft = raw['draft'];
  if (typeof draft === 'string' && UUID.test(draft)) search.draft = draft;

  const minCost = parseInteger(raw['minCost']);
  if (minCost !== undefined) search.minCost = minCost;
  const maxCost = parseInteger(raw['maxCost']);
  if (maxCost !== undefined) search.maxCost = maxCost;

  return { ...search, ...validateMoveSearch(raw) };
}

export function tabOf(search: BookingSearch): BookingTab {
  return search.tab ?? DEFAULT_TAB;
}

/** Ett filters valgte verdier, som lista kontrollen og spørringen begge leser. */
export function valuesOf(search: BookingSearch, key: FilterKey): readonly string[] {
  return parts(search[key]);
}

/** De fem flervalgsfiltrene, som nøkler i søket. Én liste, så ingen kan glemme det sjette. */
export const FILTER_KEYS = ['agency', 'genre', 'type', 'gender', 'status'] as const;
export type FilterKey = (typeof FILTER_KEYS)[number];

/**
 * Setter ett filters verdier. Tom liste fjerner parameteren helt.
 *
 * `delete` og ikke `= undefined`: ruteren skriver en `undefined`-nøkkel som `?genre=` i adressen,
 * og en tom parameter er en parameter som ser påslått ut.
 */
export function withValues(
  search: BookingSearch,
  key: FilterKey,
  values: readonly string[],
): BookingSearch {
  const next: BookingSearch = { ...search };
  if (values.length === 0) delete next[key];
  else next[key] = values.slice(0, MAX_VALUES).join(',');
  return next;
}

export function withSearchText(search: BookingSearch, text: string): BookingSearch {
  const next: BookingSearch = { ...search };
  if (text.trim() === '') delete next.q;
  else next.q = text.slice(0, MAX_SEARCH);
  return next;
}

export function withFlag(
  search: BookingSearch,
  key: 'noPrice' | 'needsIdentity' | 'hidden' | 'compared' | 'waiting',
  on: boolean,
): BookingSearch {
  const next: BookingSearch = { ...search };
  if (on) next[key] = true;
  else delete next[key];
  return next;
}

export function withCostRange(
  search: BookingSearch,
  min: number | null,
  max: number | null,
): BookingSearch {
  const next: BookingSearch = { ...search };
  if (min === null) delete next.minCost;
  else next.minCost = min;
  if (max === null) delete next.maxCost;
  else next.maxCost = max;
  return next;
}

/**
 * True når lista er SMALNET INN — som avgjør tomteksten (DESIGN.md §8) og om «Nullstill» finnes.
 *
 * Fanen og fritekstsøket teller ikke likt: et søk uten treff er også en innsnevring, og det er
 * derfor `q` er med her. Fanen er sidens emne og ikke et filter, og er derfor ute.
 */
export function isFiltered(search: BookingSearch): boolean {
  return (
    FILTER_KEYS.some((key) => valuesOf(search, key).length > 0) ||
    search.noPrice === true ||
    search.needsIdentity === true ||
    search.hidden === true ||
    search.compared === true ||
    search.waiting === true ||
    search.minCost !== undefined ||
    search.maxCost !== undefined
  );
}

/** True når noe i det hele tatt snevrer inn — inkludert fritekstsøket. */
export function isNarrowed(search: BookingSearch): boolean {
  return isFiltered(search) || (search.q ?? '') !== '';
}

/** «Nullstill» — hvert filter bort, fanen og søket urørt. Se `isFiltered` for hvorfor. */
export function withoutFilters(search: BookingSearch): BookingSearch {
  const next: BookingSearch = { ...search };
  for (const key of FILTER_KEYS) delete next[key];
  delete next.noPrice;
  delete next.needsIdentity;
  delete next.hidden;
  delete next.compared;
  delete next.waiting;
  delete next.minCost;
  delete next.maxCost;
  return next;
}
